/**
 * Chat Card Component
 *
 * Renders interactive cards embedded in chat messages.
 */

import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Chip, LinearProgress, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  AIChatCard,
  AccountStatusCardData,
  CampaignSummaryCardData,
  CampaignStatisticsCardData,
  ConfirmationCardData,
  WizardStepCardData,
  ChallengeScreenshotCardData,
  TwoFaInputCardData,
  ContractChoiceCardData,
  StatisticsCardData,
} from '../../../types/ai-chat.types';
import { useConfirmAction, useCancelAction } from '../../../hooks/use-ai-chat';
import { API_V1 } from '../../../API/APIs/APIv1';
import ChallengeScreenshotCard from './challenge-screenshot-card';
import TwoFaInputCard from './two-fa-input-card';
import ContractChoiceCard from './contract-choice-card';
import ImportProgressCard, { ImportProgressCardData } from './import-progress-card';
import UserAskCard, { UserAskCardData } from './user-ask-card';
import OpenDialogCard, { OpenDialogCardData } from './open-dialog-card';
import CredentialsRequestCard, { CredentialsRequestCardData } from './credentials-request-card';
import CampaignScheduleCard, { CampaignScheduleCardData } from './campaign-schedule-card';
import CampaignTestCard, { CampaignTestCardData } from './campaign-test-card';
import ContactDetailCard, { ContactDetailCardData } from './contact-detail-card';
import InboxCard, { InboxCardData } from './inbox-card';

interface ChatCardProps {
  card: AIChatCard;
}

const ChatCard: React.FC<ChatCardProps> = ({ card }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  switch (card.type) {
    case 'account_status':
      return <AccountStatusCard data={card.data as unknown as AccountStatusCardData} />;
    case 'campaign_summary':
      return <CampaignSummaryCard data={card.data as unknown as CampaignSummaryCardData} />;
    case 'campaign_statistics':
      return <CampaignStatisticsCard data={card.data as unknown as CampaignStatisticsCardData} />;
    case 'confirmation':
      return <ConfirmationCard data={card.data as unknown as ConfirmationCardData} />;
    case 'wizard_step':
      return <WizardStepCard data={card.data as unknown as WizardStepCardData} />;
    case 'campaign_status':
      return <CampaignStatusCard data={card.data as any} />;
    case 'statistics':
      return <StatisticsCard data={card.data as unknown as StatisticsCardData} />;
    case 'contact_list':
      return <ContactListCard data={card.data as Record<string, unknown>} />;
    case 'contact_detail':
      return <ContactDetailCard data={card.data as unknown as ContactDetailCardData} />;
    case 'inbox':
      return <InboxCard data={card.data as unknown as InboxCardData} />;
    case 'import_progress':
      return <ImportProgressCard data={card.data as unknown as ImportProgressCardData} />;
    case 'user_ask':
      return <UserAskCard data={card.data as unknown as UserAskCardData} />;
    case 'open_dialog':
      return <OpenDialogCard data={card.data as unknown as OpenDialogCardData} />;
    case 'credentials_request':
      return <CredentialsRequestCard data={card.data as unknown as CredentialsRequestCardData} />;
    case 'campaign_schedule':
      return <CampaignScheduleCard data={card.data as unknown as CampaignScheduleCardData} />;
    case 'campaign_test':
      return <CampaignTestCard data={card.data as unknown as CampaignTestCardData} />;
    case 'challenge_screenshot':
      return <ChallengeScreenshotCard data={card.data as unknown as ChallengeScreenshotCardData} />;
    case 'two_fa_input':
      return <TwoFaInputCard data={card.data as unknown as TwoFaInputCardData} />;
    case 'contract_choice':
      return <ContractChoiceCard data={card.data as unknown as ContractChoiceCardData} />;
    case 'success':
      return <AlertCard type="success" data={card.data} />;
    case 'error':
      return <AlertCard type="error" data={card.data} />;
    case 'info':
      return <AlertCard type="info" data={card.data} />;
    // Diese vier kamen vom Backend, hatten aber keine Darstellung („Unknown card type“).
    case 'campaign_cloned': {
      const d = card.data as { newName?: string; originalName?: string; copiedItems?: string[] };
      return (
        <AlertCard
          type="success"
          data={{
            title: d.newName ? `${t('campaignCloned', 'Kampagne geklont')}: ${d.newName}` : t('campaignCloned', 'Kampagne geklont'),
            message: d.originalName ? `${t('clonedFrom', 'aus')} „${d.originalName}“` : undefined,
            items: d.copiedItems,
          }}
        />
      );
    }
    case 'recommendation': {
      const d = card.data as { title?: string; issues?: string[]; recommendations?: string[] };
      return <AlertCard type="info" data={{ title: d.title, items: [...(d.issues ?? []), ...(d.recommendations ?? [])] }} />;
    }
    case 'tutorial': {
      const d = card.data as { title?: string; progress?: string | number; total?: number; steps?: Array<{ title?: string; completed?: boolean }> };
      return (
        <AlertCard
          type="info"
          data={{
            title: d.title,
            message: typeof d.progress === 'string' ? d.progress : d.total ? `${d.progress ?? 0}/${d.total}` : undefined,
            items: (d.steps ?? []).map((step) => `${step.completed ? '✓' : '○'} ${step.title ?? ''}`),
          }}
        />
      );
    }
    case 'action_buttons':
      // Die Schnellaktionen stehen auch im Antworttext; ohne eigene Darstellung nichts anzeigen.
      return null;
    default:
      if (process.env.NODE_ENV !== 'production') {
        return (
          <Paper className={classes.unknownCard}>
            <Typography variant="caption" color="textSecondary">
              Unknown card type: {card.type}
            </Typography>
          </Paper>
        );
      }
      return null;
  }
};

// Single Account Card
/** Poll cadence while a login is still in flight. Matches the import progress card. */
const ACCOUNT_POLL_INTERVAL_MS = 5000;
/** Give up after this long so a stuck login does not poll forever. */
const ACCOUNT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** Statuses that mean "something is still happening" — worth watching. */
const TRANSIENT_ACCOUNT_STATUSES = new Set(['onboarding', 'SYNCING', 'PENDING', 'pending', 'synchronizing']);

/**
 * `statusMessage` is sometimes a plain sentence and sometimes a JSON blob
 * (`{"message":…,"userAction":…,"timestamp":…}`, written by the login worker). Rendering
 * the raw string would put JSON in the status chip, so unwrap it when that is what it is.
 */
const readStatusMessage = (raw: unknown): string | undefined => {
  const value = String(raw ?? '').trim();
  if (!value) return undefined;
  if (!value.startsWith('{')) return value;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed?.message === 'string' ? parsed.message : undefined;
  } catch {
    return value;
  }
};

const SingleAccountCard: React.FC<{ account: AccountStatusCardData; classes: ReturnType<typeof useStyles>['classes'] }> = ({ account: initial, classes }) => {
  const { t } = useTranslation();

  /**
   * Keep the card current while the login runs.
   *
   * It used to render a snapshot taken at the moment the card was created, so a login that
   * finished afterwards never showed up. The assistant's own answer was "frag mich in 1–2
   * Minuten", and the user was left watching a card that would never change — reported as
   * "ich sehe gerade gar nicht was passiert" and, when the login had in fact succeeded, as
   * "der Chat sieht das anscheinend nicht".
   */
  const [account, setAccount] = useState<AccountStatusCardData>(initial);
  useEffect(() => setAccount(initial), [initial]);

  const isTransient = TRANSIENT_ACCOUNT_STATUSES.has(String(account.status || ''));

  useEffect(() => {
    if (!isTransient || !account.accountId) return undefined;

    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startedAt > ACCOUNT_POLL_TIMEOUT_MS) {
        clearInterval(interval);
        return;
      }
      API_V1.get(`/linkedin-accounts/${account.accountId}`)
        .then((response) => {
          const fresh = response.data?.account ?? response.data;
          if (!fresh?.status) return;
          setAccount((prev) => ({
            ...prev,
            status: fresh.status,
            statusLabel: readStatusMessage(fresh.statusMessage),
            lastSynced: fresh.lastLoginAt || prev.lastSynced,
          }));
        })
        .catch(() => {
          /* transient poll errors are fine — the next tick retries */
        });
    }, ACCOUNT_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isTransient, account.accountId]);

  const status = account.status || 'unknown';
  const statusLabel = account.statusLabel || String(status).replace(/_/g, ' ');
  const statusColor = {
    SYNCHRONIZED: 'success',
    active: 'success',
    SYNCING: 'info',
    onboarding: 'info',
    PENDING: 'warning',
    pending: 'warning',
    TWO_FA: 'warning',
    WRONG_CREDENTIALS: 'error',
    ERROR: 'error',
    suspended: 'error',
  }[status] || 'default';

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <PersonIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">{t('linkedInAccount', 'LinkedIn Account')}</Typography>
        <Chip
          label={statusLabel}
          size="small"
          color={statusColor as any}
          className={classes.statusChip}
        />
      </Box>
      <Box className={classes.cardContent}>
        <Typography variant="body2" className={classes.accountEmail}>
          {account.email || account.name || t('unknownAccount', 'Unknown Account')}
        </Typography>
        {account.proxyLocation && (
          <Typography variant="caption" color="textSecondary">
            {t('proxyLocation', 'Proxy')}: {account.proxyLocation}
          </Typography>
        )}
        {account.issueDescription && (
          <Typography variant="caption" color="error" className={classes.issueText}>
            {account.issueDescription}
          </Typography>
        )}
        {account.lastSynced && (
          <Typography variant="caption" color="textSecondary">
            {t('lastSynced', 'Last synced')}: {new Date(account.lastSynced).toLocaleString()}
          </Typography>
        )}
      </Box>
      {account.profileUrl && (
        <Box className={classes.cardActions}>
          <Tooltip title={t('viewOnLinkedIn', 'View on LinkedIn')}>
            <IconButton size="small" onClick={() => window.open(account.profileUrl, '_blank')}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
};

// Account Status Card - handles both single account and list of accounts
const AccountStatusCard: React.FC<{ data: AccountStatusCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Handle undefined or missing data gracefully
  if (!data) {
    return (
      <Paper className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {t('noAccountData', 'No account data available')}
        </Typography>
      </Paper>
    );
  }

  // Check if this is a list of accounts
  if (data.accounts && Array.isArray(data.accounts)) {
    // Empty accounts list
    if (data.accounts.length === 0) {
      return (
        <Paper className={classes.card}>
          <Box className={classes.cardHeader}>
            <PersonIcon className={classes.cardIcon} />
            <Typography variant="subtitle2">{t('linkedInAccounts', 'LinkedIn Accounts')}</Typography>
          </Box>
          <Box className={classes.cardContent}>
            <Typography variant="body2" color="textSecondary">
              {t('noAccountsYet', 'No LinkedIn accounts added yet. Add one to get started!')}
            </Typography>
          </Box>
        </Paper>
      );
    }

    // List of accounts - render each with SingleAccountCard
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {data.accounts.map((account, index) => (
          <SingleAccountCard
            key={account.id || account.accountId || index}
            account={{
              accountId: account.id || account.accountId,
              email: account.email,
              name: account.name,
              status: account.status,
              lastSynced: account.lastSync || account.lastSynced,
              profileUrl: account.profileUrl,
            }}
            classes={classes}
          />
        ))}
      </Box>
    );
  }

  // Single account - check if data has nested 'account' property or is the account itself
  const accountData = (data as any).account || data;
  return <SingleAccountCard account={accountData} classes={classes} />;
};

// Campaign Summary Card
/**
 * Das Backend schickt zwei Formen: eine Kampagne (`{ campaign: {…} }`, getCampaignDetails)
 * oder eine Liste (`{ campaigns: [...] }`, listCampaigns). Die Karte las beides als
 * flaches Objekt und zeigte deshalb immer „Unbenannte Kampagne · draft · 0“.
 */
type CampaignSummaryItem = {
  id?: string;
  campaignId?: string;
  name?: string;
  type?: string;
  status?: string;
  contactCount?: number;
  contactsCount?: number;
  createdAt?: string;
};

const SUMMARY_LIST_LIMIT = 10;

const CampaignSummaryCard: React.FC<{ data: CampaignSummaryCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Guard against missing data
  if (!data) {
    return (
      <Paper className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {t('noCampaignData', 'No campaign data available')}
        </Typography>
      </Paper>
    );
  }

  const raw = data as unknown as { campaign?: CampaignSummaryItem; campaigns?: CampaignSummaryItem[] } & CampaignSummaryItem;

  const statusColorOf = (status?: string) =>
    ({ active: 'success', paused: 'warning', draft: 'default', completed: 'info' } as Record<string, string>)[status || 'draft'] || 'default';
  const typeLabelOf = (type?: string) => {
    const value = (type || '').toLowerCase();
    if (!value) return '';
    return value.includes('sourcing') || value.includes('recruiting') ? t('recruiting', 'Recruiting') : t('sales', 'Sales');
  };
  const contactsOf = (item: CampaignSummaryItem) => item.contactCount ?? item.contactsCount ?? 0;

  if (Array.isArray(raw.campaigns)) {
    const campaigns = raw.campaigns;
    if (campaigns.length === 0) return null;
    const shown = campaigns.slice(0, SUMMARY_LIST_LIMIT);
    return (
      <Paper className={classes.card}>
        <Box className={classes.cardHeader}>
          <CampaignIcon className={classes.cardIcon} />
          <Typography variant="subtitle2" className={classes.campaignName}>
            {t('campaigns', 'Kampagnen')} ({campaigns.length})
          </Typography>
        </Box>
        <Box className={classes.cardContent}>
          {shown.map((item, index) => (
            <Box key={String(item.id ?? item.campaignId ?? index)} className={classes.statRow} gap={1}>
              <Box minWidth={0}>
                <Typography variant="caption" fontWeight={600} display="block" noWrap>
                  {item.name || t('unnamedCampaign', 'Unnamed Campaign')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {[typeLabelOf(item.type), `${contactsOf(item).toLocaleString()} ${t('contacts', 'Kontakte')}`].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Chip label={item.status || 'draft'} size="small" color={statusColorOf(item.status) as any} className={classes.statusChip} />
            </Box>
          ))}
          {campaigns.length > shown.length && (
            <Typography variant="caption" color="textSecondary">
              {`+ ${campaigns.length - shown.length}`}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }

  const campaign: CampaignSummaryItem = raw.campaign ?? raw;
  const typeLabel = typeLabelOf(campaign.type) || t('sales', 'Sales');

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <CampaignIcon className={classes.cardIcon} />
        <Typography variant="subtitle2" className={classes.campaignName}>
          {campaign.name || t('unnamedCampaign', 'Unnamed Campaign')}
        </Typography>
        <Chip label={campaign.status || 'draft'} size="small" color={statusColorOf(campaign.status) as any} className={classes.statusChip} />
      </Box>
      <Box className={classes.cardContent}>
        <Box className={classes.statRow}>
          <Typography variant="caption" color="textSecondary">
            {t('type', 'Type')}:
          </Typography>
          <Typography variant="caption">{typeLabel}</Typography>
        </Box>
        <Box className={classes.statRow}>
          <Typography variant="caption" color="textSecondary">
            {t('contacts', 'Contacts')}:
          </Typography>
          <Typography variant="caption">{contactsOf(campaign).toLocaleString()}</Typography>
        </Box>
        {campaign.createdAt && (
          <Box className={classes.statRow}>
            <Typography variant="caption" color="textSecondary">
              {t('created', 'Created')}:
            </Typography>
            <Typography variant="caption">{new Date(campaign.createdAt).toLocaleDateString()}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Campaign Statistics Card
const CampaignStatisticsCard: React.FC<{ data: CampaignStatisticsCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <AnalyticsIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">{data.name} - {t('statistics', 'Statistics')}</Typography>
      </Box>
      <Box className={classes.statsGrid}>
        <Box className={classes.statItem}>
          <Typography variant="h6" color="primary">
            {data.totalContacts.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('totalContacts', 'Total Contacts')}
          </Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h6" color="primary">
            {data.connectionRate.toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('connectionRate', 'Connection Rate')}
          </Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h6" color="primary">
            {data.replyRate.toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('replyRate', 'Reply Rate')}
          </Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h6" color="primary">
            {data.repliesReceived.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('replies', 'Replies')}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// Contact List Card — tolerant renderer for the various contact_list emitters
// (contacts tools, search results, campaign contacts)
const ContactListCard: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const contacts = Array.isArray(data?.contacts) ? (data.contacts as Array<Record<string, unknown>>) : [];
  const total = (data?.total ?? data?.totalAvailable ?? contacts.length) as number;

  const displayName = (c: Record<string, unknown>): string => {
    if (typeof c.name === 'string' && c.name.trim()) return c.name;
    const combined = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return combined || t('unknownContact', 'Unknown contact');
  };

  const secondaryLine = (c: Record<string, unknown>): string => {
    const role = (c.title || c.occupation || c.headline || '') as string;
    const company = (c.company || c.companyName || '') as string;
    return [role, company].filter(Boolean).join(' · ');
  };

  const profileUrl = (c: Record<string, unknown>): string | undefined => {
    const url = c.linkedinUrl || c.linkedInURL || c.profileUrl;
    return typeof url === 'string' && url.startsWith('http') ? url : undefined;
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <PersonIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">
          {(data?.title as string) || t('contacts', 'Contacts')}
        </Typography>
        <Chip label={`${total}`} size="small" className={classes.statusChip} />
      </Box>
      {typeof data?.subtitle === 'string' && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
          {data.subtitle}
        </Typography>
      )}
      {contacts.length > 0 ? (
        <Box className={classes.cardContent}>
          {contacts.map((c, index) => {
            const url = profileUrl(c);
            const secondary = secondaryLine(c);
            return (
              <Box key={(c.id as string) || (c.contactId as string) || index} className={classes.contactRow}>
                <Box className={classes.contactInfo}>
                  <Typography variant="body2" className={classes.accountEmail}>
                    {displayName(c)}
                  </Typography>
                  {secondary && (
                    <Typography variant="caption" color="textSecondary">
                      {secondary}
                    </Typography>
                  )}
                </Box>
                {typeof c.status === 'string' && (
                  <Chip label={c.status} size="small" className={classes.statusChip} />
                )}
                {url && (
                  <Tooltip title={t('viewOnLinkedIn', 'View on LinkedIn')}>
                    <IconButton size="small" onClick={() => window.open(url, '_blank')}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
          {total > contacts.length && (
            <Typography variant="caption" color="textSecondary">
              {t('andMoreContacts', '+ {{count}} more', { count: total - contacts.length })}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {t('noContactsFound', 'No contacts found')}
        </Typography>
      )}
    </Paper>
  );
};

// Generic Statistics Card — titled label/value rows from the statistics tools
const StatisticsCard: React.FC<{ data: StatisticsCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const rows = Array.isArray(data?.rows) ? data.rows : [];

  // Fallback for legacy statistics cards that ship a metrics object instead of rows
  const legacyMetrics = !rows.length && data?.metrics && typeof data.metrics === 'object'
    ? Object.entries(data.metrics as Record<string, unknown>).map(([label, value]) => ({
        label: label.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
        value: String(value),
      }))
    : [];

  const displayRows = rows.length ? rows : legacyMetrics;

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <AnalyticsIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">
          {data?.title || (data as any)?.campaignName || t('statistics', 'Statistics')}
        </Typography>
      </Box>
      {displayRows.length > 0 ? (
        <Box className={classes.cardContent}>
          {displayRows.map((row, index) => (
            <Box key={`${row.label}-${index}`} className={classes.statRow}>
              <Typography variant="caption" color="textSecondary">
                {row.label}
              </Typography>
              <Typography variant="caption" className={classes.statValue}>
                {String(row.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {t('noStatisticsData', 'No statistics data available')}
        </Typography>
      )}
    </Paper>
  );
};

// Campaign Status Card (for campaign creation feedback)
interface CampaignStatusCardData {
  campaign: {
    id: string;
    name: string;
    type: string;
    status: string;
    linkedinAccount?: string;
  };
  statusMessage?: string;
}

const CampaignStatusCard: React.FC<{ data: CampaignStatusCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const statusColor = {
    draft: 'warning',
    active: 'success',
    paused: 'default',
    completed: 'info',
  }[data.campaign.status] || 'default';

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <CampaignIcon className={classes.cardIcon} />
        <Typography variant="subtitle2" className={classes.campaignName}>
          {data.campaign.name}
        </Typography>
        <Chip label={data.campaign.status} size="small" color={statusColor as any} className={classes.statusChip} />
      </Box>
      <Box className={classes.cardContent}>
        <Box className={classes.statRow}>
          <Typography variant="caption" color="textSecondary">
            {t('type', 'Type')}:
          </Typography>
          <Typography variant="caption">{data.campaign.type}</Typography>
        </Box>
        {data.campaign.linkedinAccount && (
          <Box className={classes.statRow}>
            <Typography variant="caption" color="textSecondary">
              {t('account', 'Account')}:
            </Typography>
            <Typography variant="caption">{data.campaign.linkedinAccount}</Typography>
          </Box>
        )}
        {data.statusMessage && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            {data.statusMessage}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// Confirmation Card
const ConfirmationCard: React.FC<{ data: ConfirmationCardData }> = ({ data }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const confirmAction = useConfirmAction();
  const cancelAction = useCancelAction();

  const handleConfirm = () => {
    confirmAction.mutate({ actionId: data.actionId });
  };

  const handleCancel = () => {
    cancelAction.mutate(data.actionId);
  };

  return (
    <Paper className={cx(classes.card, classes.confirmationCard, data.destructive && 'destructive')}>
      <Box className={classes.cardHeader}>
        <WarningIcon className={cx(classes.cardIcon, data.destructive && 'warning')} />
        <Typography variant="subtitle2">{data.title}</Typography>
      </Box>
      <Box className={classes.cardContent}>
        <Typography variant="body2">{data.description}</Typography>
      </Box>
      <Box className={classes.confirmationActions}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCancel}
          disabled={confirmAction.isPending || cancelAction.isPending}
        >
          {data.cancelLabel || t('cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          color={data.destructive ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={confirmAction.isPending || cancelAction.isPending}
        >
          {data.confirmLabel || t('confirm', 'Confirm')}
        </Button>
      </Box>
    </Paper>
  );
};

// Wizard Step Card
const WizardStepCard: React.FC<{ data: WizardStepCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Handle both old (currentStep/stepName) and new (stepNumber/title) field names
  const currentStep = data.currentStep || (data as any).stepNumber || 1;
  const totalSteps = data.totalSteps || 4;
  const stepName = data.stepName || (data as any).title || 'Step';
  const stepDescription = data.stepDescription || (data as any).description || '';
  const fields = (data as any).fields as Array<{
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: Array<{ value: string; label: string }>;
  }> | undefined;

  const progress = (currentStep / totalSteps) * 100;

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <Typography variant="subtitle2">
          {t('step', 'Step')} {currentStep} / {totalSteps}: {stepName}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={progress} className={classes.wizardProgress} />
      <Box className={classes.cardContent}>
        {stepDescription && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: fields ? 2 : 0 }}>
            {stepDescription}
          </Typography>
        )}
        {fields && fields.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {fields.map((field) => (
              <Typography key={field.name} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{field.label}</strong>: {field.required ? '(required)' : '(optional)'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Alert Card (success, error, info)
const AlertCard: React.FC<{ type: 'success' | 'error' | 'info'; data: Record<string, unknown> }> = ({
  type,
  data,
}) => {
  const { classes, cx } = useStyles();

  const icons: Record<'success' | 'error' | 'info', React.ReactNode> = {
    success: <CheckCircleIcon />,
    error: <ErrorIcon />,
    info: <InfoIcon />,
  };

  return (
    <Paper className={cx(classes.card, classes.alertCard, type)}>
      <Box className={classes.alertContent}>
        <Box className={cx(classes.alertIcon, type)}>{icons[type]}</Box>
        <Box>
          {data.title != null && (
            <Typography variant="subtitle2" className={classes.alertTitle}>
              {String(data.title)}
            </Typography>
          )}
          {data.message != null && <Typography variant="body2">{String(data.message)}</Typography>}
          {Array.isArray(data.items) && data.items.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {(data.items as unknown[]).map((item, index) => (
                <Typography key={index} component="li" variant="body2">
                  {String(item)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
  },
  unknownCard: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardIcon: {
    fontSize: '1.25rem',
    color: theme.palette.primary.main,
    '&.warning': {
      color: theme.palette.warning.main,
    },
  },
  statusChip: {
    marginLeft: 'auto',
    height: 22,
    fontSize: '0.7rem',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
  },
  accountEmail: {
    fontWeight: 500,
  },
  issueText: {
    display: 'block',
    marginTop: theme.spacing(0.5),
  },
  campaignName: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statRow: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  statValue: {
    fontWeight: 500,
    textAlign: 'right',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  statItem: {
    textAlign: 'center',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 6,
  },
  confirmationCard: {
    borderColor: theme.palette.warning.light,
    '&.destructive': {
      borderColor: theme.palette.error.light,
    },
  },
  confirmationActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5),
  },
  wizardProgress: {
    marginBottom: theme.spacing(1),
    borderRadius: 4,
  },
  alertCard: {
    '&.success': {
      borderColor: theme.palette.success.light,
      backgroundColor: theme.palette.success.light + '10',
    },
    '&.error': {
      borderColor: theme.palette.error.light,
      backgroundColor: theme.palette.error.light + '10',
    },
    '&.info': {
      borderColor: theme.palette.info.light,
      backgroundColor: theme.palette.info.light + '10',
    },
  },
  alertContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  alertIcon: {
    '& svg': {
      fontSize: '1.5rem',
    },
    '&.success svg': {
      color: theme.palette.success.main,
    },
    '&.error svg': {
      color: theme.palette.error.main,
    },
    '&.info svg': {
      color: theme.palette.info.main,
    },
  },
  alertTitle: {
    marginBottom: theme.spacing(0.5),
  },
}));

export default ChatCard;

